import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleArrowDown, Edit, Trash2, Copy, ExternalLink, ChevronLeft, ChevronRight, Bell, Map, Search, ArrowRight, Filter, X, Check } from "lucide-react";
import { motion, AnimatePresence } from 'framer-motion';
import CustomSelect from '../../../../components/forms/CustomSelect';
import VoucherEmptyState from './VoucherEmptyState';
import vouchersData from '../../../../data/vouchers/vouchersData.json';

const voucherStatuses = vouchersData.voucherStatuses || [];

const formatDestinationSummary = (client) => {
    let destList = [];
    if (client?.tour_info?.destinations?.length > 0) {
        destList = client.tour_info.destinations
            .map((d) => (typeof d === 'string' ? d : (d?.city || d?.location_name || '')))
            .filter(Boolean);
    } else if (client?.destination && client.destination !== 'N/A') {
        destList = client.destination.split(',').map((s) => s.trim()).filter(Boolean);
    }

    if (destList.length === 0) {
        return { first: 'N/A', remaining: 0, fullText: 'N/A' };
    }

    return {
        first: destList[0],
        remaining: destList.length - 1,
        fullText: destList.join(', ')
    };
};

const VoucherActions = ({
    client,
    generateAndCopyMessage,
    handleDownload,
    handlePingUser,
    handleEditClick,
    handleDeleteClick
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpwards, setOpenUpwards] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 250) { // Dropdown height is approx 240px
                setOpenUpwards(true);
            } else {
                setOpenUpwards(false);
            }
        }
    }, [isOpen]);

    const subUserRole = localStorage.getItem('sub_user_role');
    const isBookingsRole = subUserRole === 'bookings';

    const actions = [
        {
            icon: <Copy size={15} />,
            label: 'Copy Message',
            onClick: () => { generateAndCopyMessage(client); setIsOpen(false); },
            isDelete: false
        },
        ...(!isBookingsRole ? [{
            icon: <Map size={16} />,
            label: 'Track Route',
            onClick: () => { window.location.href = `/tracking/map?voucherId=${client.bookingID}`; setIsOpen(false); },
            color: 'text-emerald-600',
            hoverBg: 'hover:bg-emerald-50'
        }] : []),
        {
            icon: <CircleArrowDown size={15} />,
            label: 'Download PDF',
            onClick: () => { handleDownload(client.id, `${client.clientName}_Voucher.pdf`, client.itineraryLink); setIsOpen(false); },
            isDelete: false
        },
        {
            icon: <Bell size={15} />,
            label: 'Ping User',
            onClick: () => { handlePingUser(client.bookingID); setIsOpen(false); },
            isDelete: false
        },
        {
            icon: <Edit size={15} />,
            label: 'Edit Voucher',
            onClick: () => { handleEditClick(client.id); setIsOpen(false); },
            isDelete: false
        },
        ...(!isBookingsRole ? [{
            icon: <Trash2 size={15} />,
            label: 'Delete Voucher',
            onClick: () => { handleDeleteClick(client.id); setIsOpen(false); },
            isDelete: true
        }] : [])
    ];

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                ref={buttonRef}
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                className={`px-4 py-2 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-xs font-semibold ${isOpen
                    ? 'bg-[#067978] text-white shadow-lg shadow-[#067978]/20 scale-95'
                    : 'text-slate-500 bg-slate-50 border border-slate-100 hover:text-slate-700 hover:bg-slate-100'
                    }`}
                title="Options"
            >
                <span>Options</span>
                <ChevronRight size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: openUpwards ? 15 : -15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: openUpwards ? 15 : -15 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className={`absolute right-0 w-48 bg-white rounded-xl shadow-xl border border-slate-200/60 py-1.5 z-[1000] ${openUpwards ? 'bottom-full mb-2 origin-bottom-right' : 'top-full mt-2 origin-top-right'
                            }`}
                    >
                        {actions.map((action, idx) => (
                            <button
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick();
                                }}
                                className={`w-full group flex items-center gap-3 px-4 py-2 text-[13px] font-medium transition-all ${action.isDelete
                                        ? 'text-slate-600 hover:bg-rose-50 hover:text-rose-600'
                                        : 'text-slate-600 hover:bg-[#067978]/5 hover:text-[#067978]'
                                    }`}
                            >
                                <span className={`transition-all duration-200 ${action.isDelete ? 'text-rose-500 group-hover:scale-110' : 'text-[#067978] opacity-80 group-hover:opacity-100 group-hover:scale-110'}`}>{action.icon}</span>
                                <span className="transition-transform duration-200">{action.label}</span>
                            </button>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const VoucherTable = ({
    currentVouchers,
    currentPage,
    totalPages,
    paginate,
    totalItems,
    handleViewVoucher,
    generateAndCopyMessage,
    handleDownload,
    handleEditClick,
    handleDeleteClick,
    handlePingUser,
    localSearchTerm,
    setLocalSearchTerm,
    statusFilter,
    handleStatusChange
}) => {
    const navigate = useNavigate();
    const shouldShowEmptyState = totalItems === 0 && !localSearchTerm && statusFilter === 'all';
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(Boolean(localSearchTerm));
    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const mobileFilterRef = useRef(null);
    const mobileFilterBtnRef = useRef(null);

    const isFilterActive = statusFilter && statusFilter !== 'all';

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                mobileFilterRef.current &&
                !mobileFilterRef.current.contains(event.target) &&
                mobileFilterBtnRef.current &&
                !mobileFilterBtnRef.current.contains(event.target)
            ) {
                setIsMobileFilterOpen(false);
            }
        };
        if (isMobileFilterOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isMobileFilterOpen]);

    return (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 flex flex-col justify-between min-h-[500px] overflow-hidden">
            <div>
                {/* Desktop Header: Search Bar on Left, All Status Filter & Limit Badge on Right (md and above) */}
                <div className="p-4 sm:p-5 border-b border-slate-100 hidden md:flex items-center gap-3 sm:gap-4 bg-white">
                    <div className="relative flex-1 w-full min-w-0">
                        <input
                            type="text"
                            placeholder="Search vouchers..."
                            value={localSearchTerm}
                            onChange={(e) => setLocalSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-8 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978] transition-all text-slate-600"
                        />
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <Search size={15} />
                        </div>
                        {localSearchTerm && (
                            <button
                                type="button"
                                onClick={() => setLocalSearchTerm('')}
                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                                aria-label="Clear search"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <div className="w-48 shrink-0">
                            <CustomSelect
                                value={statusFilter}
                                onChange={(val) => handleStatusChange({ target: { value: val } })}
                                options={voucherStatuses}
                                placeholder="All Status"
                                buttonClassName="px-4 py-2.5 text-xs"
                            />
                        </div>
                    </div>
                </div>

                {/* Mobile Header: Compact Row with Badge & Action Icons (< md) */}
                <div className="p-3.5 sm:p-4 border-b border-slate-100 md:hidden bg-white flex flex-col gap-2.5">
                    <div className="flex items-center justify-end gap-2">

                        {/* Mobile Action Buttons: Search Icon & Filter Icon */}
                        <div className="flex items-center gap-2">
                            {/* Search Icon Toggle */}
                            <button
                                type="button"
                                onClick={() => setIsMobileSearchOpen((prev) => !prev)}
                                className={`relative p-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-center ${
                                    isMobileSearchOpen || localSearchTerm
                                        ? 'bg-[#067978] text-white border-[#067978] shadow-xs shadow-[#067978]/20'
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                                title="Search vouchers"
                                aria-label="Search vouchers"
                            >
                                <Search size={15} />
                                {localSearchTerm && !isMobileSearchOpen && (
                                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white" />
                                )}
                            </button>

                            {/* Filter Icon Dropdown */}
                            <div className="relative">
                                <button
                                    ref={mobileFilterBtnRef}
                                    type="button"
                                    onClick={() => setIsMobileFilterOpen((prev) => !prev)}
                                    className={`relative p-2.5 rounded-xl border transition-all cursor-pointer flex items-center justify-center ${
                                        isMobileFilterOpen || isFilterActive
                                            ? 'bg-[#067978] text-white border-[#067978] shadow-xs shadow-[#067978]/20'
                                            : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                    }`}
                                    title="Filter vouchers by status"
                                    aria-label="Filter vouchers by status"
                                >
                                    <Filter size={15} />
                                    {isFilterActive && (
                                        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-amber-500 rounded-full border-2 border-white" />
                                    )}
                                </button>

                                {/* Dropdown Menu */}
                                <AnimatePresence>
                                    {isMobileFilterOpen && (
                                        <motion.div
                                            ref={mobileFilterRef}
                                            initial={{ opacity: 0, y: -6, scale: 0.95 }}
                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                            exit={{ opacity: 0, y: -6, scale: 0.95 }}
                                            transition={{ duration: 0.15, ease: "easeOut" }}
                                            className="absolute right-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-200/80 py-1.5 z-50 overflow-hidden"
                                        >
                                            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100 mb-1 flex items-center justify-between">
                                                <span>Filter by status</span>
                                                {isFilterActive && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleStatusChange({ target: { value: 'all' } });
                                                            setIsMobileFilterOpen(false);
                                                        }}
                                                        className="text-[#067978] hover:underline font-semibold text-[10px] lowercase cursor-pointer"
                                                    >
                                                        reset
                                                    </button>
                                                )}
                                            </div>
                                            <div className="max-h-52 overflow-y-auto p-1 space-y-0.5">
                                                {voucherStatuses.map((opt) => {
                                                    const isSelected = String(statusFilter) === String(opt.value);
                                                    return (
                                                        <button
                                                            key={opt.value}
                                                            type="button"
                                                            onClick={() => {
                                                                handleStatusChange({ target: { value: opt.value } });
                                                                setIsMobileFilterOpen(false);
                                                            }}
                                                            className={`w-full px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-between transition-all text-left cursor-pointer ${
                                                                isSelected
                                                                    ? 'bg-[#067978]/10 text-[#067978] font-bold'
                                                                    : 'text-slate-700 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {isSelected && <Check size={14} className="text-[#067978] stroke-[2.5]" />}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>

                    {/* Expanding Mobile Search Bar */}
                    <AnimatePresence>
                        {isMobileSearchOpen && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                                className="overflow-hidden"
                            >
                                <div className="relative flex items-center gap-2 pt-1">
                                    <div className="relative flex-1">
                                        <input
                                            type="text"
                                            placeholder="Search vouchers..."
                                            value={localSearchTerm}
                                            onChange={(e) => setLocalSearchTerm(e.target.value)}
                                            autoFocus
                                            className="w-full pl-8 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-[#067978]/20 focus:border-[#067978] transition-all text-slate-600"
                                        />
                                        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                                            <Search size={14} />
                                        </div>
                                        {localSearchTerm && (
                                            <button
                                                type="button"
                                                onClick={() => setLocalSearchTerm('')}
                                                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                                                aria-label="Clear search"
                                            >
                                                <X size={13} />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsMobileSearchOpen(false);
                                            if (localSearchTerm) setLocalSearchTerm('');
                                        }}
                                        className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer shrink-0"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Conditional Rendering: If no vouchers in database */}
                {shouldShowEmptyState ? (
                    <VoucherEmptyState />
                ) : (
                    <>
                        {/* Desktop Table View */}
                        <div className="hidden lg:block">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-white border-b border-slate-100 text-xs font-bold text-slate-400">
                                        <th className="px-6 py-3.5 rounded-tl-3xl whitespace-nowrap">Sr no.</th>
                                        <th className="px-6 py-3.5">Client Name</th>
                                        <th className="px-6 py-3.5">Booking ID</th>
                                        <th className="px-6 py-3.5 text-center">Voucher</th>
                                        <th className="px-6 py-3.5 text-center">Dashboard</th>
                                        <th className="px-6 py-3.5 text-right pr-10 rounded-tr-3xl">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {currentVouchers.length > 0 ? (
                                        currentVouchers.map((client, index) => {
                                            return (
                                                <tr
                                                    key={client.id}
                                                    className="group hover:bg-slate-50/80 transition-all duration-200"
                                                >
                                                    <td className="px-6 py-4 text-sm text-slate-400 font-medium">{(currentPage - 1) * 10 + index + 1}</td>
                                                    <td className="px-6 py-4 text-sm font-semibold text-slate-700">
                                                        <span>{client.clientName}</span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500 font-mono tracking-tight">{client.bookingID}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div className="flex flex-col items-center gap-1.5">
                                                            {client.itineraryLink && client.itineraryLink.startsWith('http') ? (
                                                                <button
                                                                    onClick={() => handleViewVoucher(client.id, client.itineraryLink)}
                                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-all"
                                                                    title="Open PDF"
                                                                >
                                                                    <ExternalLink size={14} />
                                                                    View Voucher
                                                                </button>
                                                            ) : (
                                                                <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest italic">Pending</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-center">
                                                        <button
                                                            onClick={() => navigate(`/voucher-dashboard/${client.id}`)}
                                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/5 rounded-lg hover:bg-primary/10 transition-all"
                                                        >
                                                            View Trip
                                                        </button>
                                                    </td>
                                                    <td className="px-6 py-4 text-right pr-6">
                                                        <div className="flex justify-end items-center gap-3">
                                                            <VoucherActions
                                                                client={client}
                                                                generateAndCopyMessage={generateAndCopyMessage}
                                                                handleDownload={handleDownload}
                                                                handlePingUser={handlePingUser}
                                                                handleEditClick={handleEditClick}
                                                                handleDeleteClick={handleDeleteClick}
                                                            />
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="6" className="py-20 text-center">
                                                <p className="text-sm font-semibold text-slate-300 uppercase tracking-widest">No vouchers match your filter</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile Card View */}
                        <div className="lg:hidden p-4 space-y-3.5">
                            {currentVouchers.length > 0 ? (
                                currentVouchers.map((client) => (
                                    <div
                                        key={client.id}
                                        onClick={() => navigate(`/voucher-dashboard/${client.id}`)}
                                        className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-[0.99]"
                                    >
                                        {/* Top Row: Guest Info & Options Menu */}
                                        <div className="flex justify-between items-start gap-2 mb-3">
                                            <div className="min-w-0 flex-1">
                                                <h3 className="font-semibold text-slate-700 text-base truncate">
                                                    {client.clientName || "Guest"}
                                                </h3>
                                                <p className="text-slate-400 text-xs mt-1 font-mono bg-white inline-block px-1.5 py-0.5 rounded border border-slate-100 font-semibold">
                                                    {client.bookingID}
                                                </p>
                                            </div>
                                            <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                                                <VoucherActions
                                                    client={client}
                                                    generateAndCopyMessage={generateAndCopyMessage}
                                                    handleDownload={handleDownload}
                                                    handlePingUser={handlePingUser}
                                                    handleEditClick={handleEditClick}
                                                    handleDeleteClick={handleDeleteClick}
                                                />
                                            </div>
                                        </div>

                                        {/* Details Rows: Destination & PDF Voucher */}
                                        <div className="space-y-2.5 text-xs font-medium border-t border-slate-100 pt-3 mt-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <span className="text-slate-400 font-medium shrink-0">Destination</span>
                                                {(() => {
                                                    const destSummary = formatDestinationSummary(client);
                                                    if (destSummary.first === 'N/A') {
                                                        return <span className="text-slate-400 font-medium">N/A</span>;
                                                    }
                                                    return (
                                                        <div
                                                            className="flex items-center gap-1 min-w-0 max-w-[70%] justify-end text-right"
                                                            title={destSummary.fullText}
                                                        >
                                                            <span className="text-slate-700 font-semibold truncate">
                                                                {destSummary.first}{destSummary.remaining > 0 ? '...' : ''}
                                                            </span>
                                                            {destSummary.remaining > 0 && (
                                                                <span className="text-[#067978] font-bold text-xs shrink-0">
                                                                    +{destSummary.remaining}
                                                                </span>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                                                <span className="text-slate-400 font-medium">Voucher PDF</span>
                                                {client.itineraryLink && client.itineraryLink.startsWith('http') ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleViewVoucher(client.id, client.itineraryLink)}
                                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline cursor-pointer"
                                                    >
                                                        <ExternalLink size={12} />
                                                        <span>View Voucher</span>
                                                    </button>
                                                ) : (
                                                    <span className="text-slate-400 text-xs italic font-medium">Pending</span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Primary Action Button */}
                                        <div className="mt-3.5 pt-2 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/voucher-dashboard/${client.id}`)}
                                                className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 px-4 text-xs font-bold text-white bg-[#067978] hover:bg-[#045d5c] rounded-xl transition-all shadow-xs active:scale-95 cursor-pointer whitespace-nowrap"
                                            >
                                                <span>View Trip</span>
                                                <ArrowRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-20 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200">
                                    <p className="text-sm font-semibold text-slate-300 uppercase tracking-widest">No vouchers match your filter</p>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Pagination Controls */}
            {!shouldShowEmptyState && totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-3xl">
                    <span className="text-xs font-medium text-slate-400 text-center sm:text-left">
                        Showing <span className="text-slate-700 font-semibold">{(currentPage - 1) * 10 + 1}</span> to <span className="text-slate-700 font-semibold">{Math.min(currentPage * 10, totalItems)}</span> of <span className="text-slate-700 font-semibold">{totalItems}</span> entries
                    </span>
                    <div className="flex items-center gap-2 flex-wrap justify-center">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={`p-2 rounded-xl border flex items-center justify-center transition-all ${currentPage === 1
                                ? 'border-slate-100 text-slate-300 cursor-not-allowed bg-white'
                                : 'border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 bg-white shadow-sm'
                                }`}
                        >
                            <ChevronLeft size={16} />
                        </button>

                        <div className="flex items-center gap-1">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => {
                                if (
                                    number === 1 ||
                                    number === totalPages ||
                                    (number >= currentPage - 1 && number <= currentPage + 1)
                                ) {
                                    return (
                                        <button
                                            key={number}
                                            onClick={() => paginate(number)}
                                            className={`w-8 h-8 rounded-xl text-xs font-semibold flex items-center justify-center transition-all ${currentPage === number
                                                ? 'bg-primary text-white shadow-md shadow-primary/20'
                                                : 'text-slate-500 hover:bg-slate-100 bg-transparent'
                                                }`}
                                        >
                                            {number}
                                        </button>
                                    );
                                } else if (
                                    number === currentPage - 2 ||
                                    number === currentPage + 2
                                ) {
                                    return <span key={number} className="text-slate-300 px-1">...</span>;
                                }
                                return null;
                            })}
                        </div>

                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={`p-2 rounded-xl border flex items-center justify-center transition-all ${currentPage === totalPages
                                ? 'border-slate-100 text-slate-300 cursor-not-allowed bg-white'
                                : 'border-slate-200 text-slate-600 hover:bg-slate-100 hover:border-slate-300 bg-white shadow-sm'
                                }`}
                        >
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VoucherTable;
